/* eslint-disable quotes */
import React, { useState } from "react";
import {
  Card,
  Divider,
  Flex,
  Input,
  Select,
  Row,
  Col,
  Space,
  Upload,
  Typography,
  message
} from "antd";
import { PaperClipOutlined } from "@ant-design/icons";

import LexicalTemplate from "@app/features/system-setup/ui/message-templates/message-template-modal/lexical-template";
import Button from "@ui/form-controls/button";
import RedStar from "@ui/form-controls/required-indicator";
import { decodeRecipientPlaceholders } from "@app/features/system-setup/ui/message-templates/message-template-modal/helper";
import { RxCrop } from "react-icons/rx";

import { useDidMountEffect } from "@app/shared/hooks/useDidMountEffect";
import { convertResToArr } from "utils/reusableFunctions";

import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  addMessageTemplate,
  clearSendMessageTemplateEmail,
  sendMessageTemplateEmail
} from "@redux/actionCreators/bookingSetup";

const { Text } = Typography;
const defaultTemplate = "createNew";

/**
 * Form Fields to write the email content
 *
 * @param {Object} props - component Props
 * @param {Array<{ObjectId: string, Name: string}>} [props.selectedPersons] - List of selected persons.
 * @param {Array<{ObjectId: string, Name: string}>} [props.persons] - List of persons.
 * @param {Function} [props.resetModalFn] - Function to reset/close the modal.
 * @returns {JSX.Element}
 */
const PersonEmailTemplateForm = ({ persons, resetModalFn, selectedPersons }) => {
  const dispatch = useDispatch();

  const [fieldErrors, setFieldErrors] = useState({ msgTemplateBody: null });
  const [selectedTemplate, setSelectedTemplate] = useState(defaultTemplate);
  const [templateFields, setTemplateFields] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [emailTemplateInputs, setEmailTemplateInputs] = useState({
    templateName: "",
    fileArray: [],
    templateMessageHTML: "",
    templateSubject: ""
  });
  const { templateName, fileArray, templateMessageHTML, templateSubject } = emailTemplateInputs;

  const bookingSetupReducer = useSelector(state => state.bookingSetupReducer || null, shallowEqual);

  const {
    addMessageTemplate: {
      isLoading: addMessageTemplateLoading,
      resultData: addMessageTemplateData
    },
    sendMessageTemplateEmail: {
      isLoading: sendMessageTemplateEmailLoading,
      resultData: sendMessageTemplateEmailData
    },
    getMessageTemplates: {
      isLoading: getMessageTemplatesLoading,
      resultData: getMessageTemplatesData
    },
    getTemplateFields: { isLoading: getTemplateFieldsLoading, resultData: getTemplateFieldsData }
  } = bookingSetupReducer;

  const requestLoading =
    addMessageTemplateLoading ||
    sendMessageTemplateEmailLoading ||
    getMessageTemplatesLoading ||
    getTemplateFieldsLoading;

  const handleMessageTemplateHTML = ({ html }) => {
    const enhancedHtml = html.replaceAll(' style="white-space: pre-wrap;"', "");

    setEmailTemplateInputs(prev => ({
      ...prev,
      templateMessageHTML: enhancedHtml
    }));
  };

  const handleTemplateSelect = templateId => {
    const template = templates.find(t => t.ObjectId === templateId);
    if (template) {
      setSelectedTemplate(template.ObjectId);
      setEmailTemplateInputs(prev => ({
        ...prev,
        templateName: template?.Name || "",
        templateMessageHTML: template?.Body.replace(/<br\s*\/?>/gi, "\n") || "",
        templateSubject: template?.Title || "",
        fileArray: template?.Attachments
          ? convertResToArr(template?.Attachments.msgEmailAttachmentItem).map(item => ({
              ...item,
              name: item.Name,
              status: "done"
            }))
          : []
      }));
    } else {
      setSelectedTemplate(defaultTemplate);
      setEmailTemplateInputs({
        templateName: "",
        templateMessageHTML: "",
        templateSubject: "",
        fileArray: []
      });
    }
  };

  const handleSubmit = () => {
    if (!persons.length) {
      message.error("Please add at least one recipient.");
      return;
    }

    if (templateMessageHTML.trim() === "") {
      setFieldErrors(prev => ({
        ...prev,
        msgTemplateBody: "Email body is required."
      }));
      return;
    } else {
      setFieldErrors(prev => ({
        ...prev,
        msgTemplateBody: null
      }));
    }

    const trimmedBody = templateMessageHTML.replace(/\n/g, "<br/>");
    const decodedBody = decodeRecipientPlaceholders(trimmedBody, "Person");
    const formData = new FormData();

    const params = {
      PersonObjectIds: selectedPersons.map(person => person.ObjectId),
      OverrideTitle: templateSubject,
      OverrideBody: decodedBody
    };

    if (selectedTemplate && selectedTemplate !== "createNew") {
      params.TemplateObjectId = selectedTemplate;
    }

    if (fileArray.length) {
      const fileNames = [];
      fileArray.forEach(file => {
        if (!file.EmailAttachmentId) {
          fileNames.push(file.name);
          formData.append("files", file);
        }
      });
      params.FileNames = fileNames;
    }

    formData.append("requestObject", JSON.stringify(params));

    dispatch(
      sendMessageTemplateEmail({
        payload: formData
      })
    );
  };

  const saveTemplate = () => {
    if (templateMessageHTML.trim() === "") {
      setFieldErrors(prev => ({
        ...prev,
        msgTemplateBody: "Email body is required."
      }));
      return;
    } else {
      setFieldErrors(prev => ({
        ...prev,
        msgTemplateBody: null
      }));
    }

    const trimmedBody = templateMessageHTML.replace(/\n+$/, "<br />");
    const decodedBody = decodeRecipientPlaceholders(trimmedBody, "Person");
    const fileNames = [];
    const params = {
      Title: templateSubject,
      Name: templateName,
      RecipientType: "Person",
      Body: decodedBody,
      FileNames: fileNames
    };

    const formData = new FormData();

    if (fileArray.length) {
      fileArray.forEach(file => {
        fileNames.push(file.name);
        formData.append("files", file);
      });
    }
    dispatch(
      addMessageTemplate({
        params: encodeURIComponent(JSON.stringify(params)),
        payload: formData
      })
    );
  };

  // handle sent email
  useDidMountEffect(() => {
    if (sendMessageTemplateEmailData && sendMessageTemplateEmailData.Response === "Success") {
      resetModalFn();
      dispatch(clearSendMessageTemplateEmail());
    }

    if (
      (sendMessageTemplateEmailData && sendMessageTemplateEmailData.Response === "Success") ||
      (addMessageTemplateData && addMessageTemplateData.Response === "Success")
    ) {
      setEmailTemplateInputs(prev => ({
        ...prev,
        templateMessageHTML: "",
        templateSubject: "",
        templateName: "",
        fileArray: []
      }));
    }
  }, [sendMessageTemplateEmailData, addMessageTemplateData]);

  // set templates fields
  useDidMountEffect(() => {
    if (
      getTemplateFieldsData &&
      getTemplateFieldsData.Response === "Success" &&
      getTemplateFieldsData.MessageTemplates.length
    ) {
      const templates = getTemplateFieldsData.MessageTemplates.filter(
        ({ TargetEntity }) => TargetEntity === "Person"
      ).map(({ FieldName }) => FieldName);
      setTemplateFields(templates);
    }
  }, [getTemplateFieldsData]);

  // set templates list
  useDidMountEffect(() => {
    if (
      getMessageTemplatesData &&
      getMessageTemplatesData.Response === "Success" &&
      getMessageTemplatesData.MessageTemplates.length
    ) {
      let tableData = [];
      tableData = getMessageTemplatesData?.MessageTemplates.filter(
        ({ TargetEntityType }) => TargetEntityType === "Person"
      );
      setTemplates(tableData);
    }
  }, [getMessageTemplatesData]);

  const fileUploadProps = {
    name: "file",
    multiple: true,
    action: "",
    fileList: fileArray,
    showUploadList: {
      showRemoveIcon: true,
      removeIcon: file => {
        return file.EmailAttachmentId ? (
          <RxCrop style={{ cursor: "not-allowed", opacity: 0, pointerEvents: "none" }} />
        ) : null;
      }
    },
    beforeUpload: file => {
      setEmailTemplateInputs(prev => ({ ...prev, fileArray: [...prev.fileArray, file] }));
    },
    onRemove: file => {
      if (file?.EmailAttachmentId) {
        return false;
      }

      const filteredFiles = fileArray.filter(item =>
        file.EmailAttachmentId ? true : file.name !== item.name
      );
      setEmailTemplateInputs(prev => ({ ...prev, fileArray: filteredFiles }));
    }
  };

  return (
    <Flex vertical>
      <Card>
        <Flex vertical gap="small">
          <Flex gap="large" justify="flex-start" align="center">
            <Flex gap="small" align="center" className="basis-2/5 whitespace-nowrap">
              <Text>Email Template</Text>
              <Select
                style={{ width: "100%" }}
                placeholder="Select a template"
                value={selectedTemplate}
                onChange={handleTemplateSelect}
                options={[{ Title: "+ Create New", ObjectId: "createNew" }, ...templates].map(
                  t => ({ label: t.Title, value: t.ObjectId })
                )}
              />
            </Flex>

            <Flex gap="small" align="center" className="basis-2/5 whitespace-nowrap">
              <Text>
                Template Name <RedStar />
              </Text>
              <Input
                disabled={selectedTemplate !== "createNew"}
                placeholder="Name"
                value={templateName}
                onChange={e =>
                  setEmailTemplateInputs(prev => ({ ...prev, templateName: e.target.value }))
                }
              />
            </Flex>
          </Flex>
        </Flex>

        <Divider />

        <Flex className="whitespace-nowrap w-2/5 mb-4" align="center" gap="small">
          <Text>
            Email Subject <RedStar />
          </Text>
          <Input
            placeholder="Subject"
            value={templateSubject}
            onChange={e =>
              setEmailTemplateInputs(prev => ({ ...prev, templateSubject: e.target.value }))
            }
          />
        </Flex>

        <LexicalTemplate
          templateFields={templateFields}
          handleMessageTemplateHTML={handleMessageTemplateHTML}
          templateMessageHTML={templateMessageHTML}
        />
        <Row justify="end">
          {fieldErrors.msgTemplateBody && <Text type="danger">{fieldErrors.msgTemplateBody}</Text>}
        </Row>

        <div className="email-template-controller-container email-template-attachment">
          <Text className="email-template-controller-title">Attach Document</Text>
          <div className="email-template-clip-icon-container">
            <Upload {...fileUploadProps}>
              <PaperClipOutlined className="clip-icon" />
            </Upload>
          </div>
        </div>
      </Card>

      <Row style={{ marginTop: "10px" }} justify="space-between">
        <Col>
          {selectedTemplate === "createNew" && (
            <Button
              variant="primary"
              disabled={
                templateName === "" ||
                templateSubject === "" ||
                templateMessageHTML === "" ||
                requestLoading
              }
              onClick={saveTemplate}
            >
              Save
            </Button>
          )}
        </Col>

        <Col>
          <Space>
            <Button variant="outline" onClick={() => resetModalFn()}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={templateSubject === "" || templateMessageHTML === "" || requestLoading}
              onClick={handleSubmit}
            >
              Send
            </Button>
          </Space>
        </Col>
      </Row>
    </Flex>
  );
};

export default React.memo(PersonEmailTemplateForm);